import type { VaultFileItem, VaultFolderNode } from "@/types/vault.types";
import { isFolder } from "@/types/vault.types";
import type { VaultNavId, VaultSourceId } from "@/types/vaultNav.types";
import type { BreadcrumbSegment } from "@/types/vault.types";

export function countFiles(node: VaultFolderNode | VaultFileItem): number {
  if (!isFolder(node)) return 1;
  return node.children.reduce((sum, child) => sum + countFiles(child), 0);
}

export function resolveFolderAtPath(root: VaultFolderNode, path: string[]): VaultFolderNode | null {
  let current = root;
  for (const id of path) {
    const child = current.children.find((c) => isFolder(c) && c.id === id) as VaultFolderNode | undefined;
    if (!child) return null;
    current = child;
  }
  return current;
}

export function resolvePathFolderNames(root: VaultFolderNode, path: string[]): string[] {
  const names: string[] = [];
  let current = root;
  for (const id of path) {
    const child = current.children.find((c) => isFolder(c) && c.id === id) as VaultFolderNode | undefined;
    if (!child) return names;
    names.push(child.name);
    current = child;
  }
  return names;
}

export function flattenFiles(root: VaultFolderNode): VaultFileItem[] {
  const files: VaultFileItem[] = [];
  const walk = (node: VaultFolderNode | VaultFileItem) => {
    if (isFolder(node)) {
      node.children.forEach(walk);
    } else {
      files.push(node);
    }
  };
  walk(root);
  return files;
}

export function getSourceRoot(
  sourceId: VaultSourceId,
  eqRoot: VaultFolderNode,
  gdriveRoot: VaultFolderNode | null,
  dropboxRoot: VaultFolderNode | null,
): VaultFolderNode | null {
  switch (sourceId) {
    case "eq":
      return eqRoot;
    case "gdrive":
      return gdriveRoot;
    case "dropbox":
      return dropboxRoot;
    default:
      return eqRoot;
  }
}

export function buildBreadcrumbs(
  sourceLabel: string,
  folderPath: string[],
  root: VaultFolderNode,
  extra?: { label: string }[],
): BreadcrumbSegment[] {
  const crumbs: BreadcrumbSegment[] = [{ label: "Vault", path: [] }];
  if (extra) {
    for (const e of extra) crumbs.push({ label: e.label, path: [] });
  } else {
    crumbs.push({ label: sourceLabel, path: [] });
  }

  if (folderPath.length === 0) return crumbs;

  let current = root;
  const pathSoFar: string[] = [];
  for (const id of folderPath) {
    const child = current.children.find((c) => isFolder(c) && c.id === id) as VaultFolderNode | undefined;
    if (!child) break;
    pathSoFar.push(id);
    crumbs.push({ label: child.name, path: [...pathSoFar] });
    current = child;
  }
  return crumbs;
}

export function filterByNav(
  folders: VaultFolderNode[],
  files: VaultFileItem[],
  navId: VaultNavId,
  isDemo: boolean,
): { folders: VaultFolderNode[]; files: VaultFileItem[] } {
  switch (navId) {
    case "all":
      return { folders, files };
    case "recent":
      return {
        folders: [],
        files: [...files].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 12),
      };
    case "starred":
      if (!isDemo) return { folders: [], files: [] };
      return {
        folders: folders.filter((f) => f.starred),
        files: files.filter((f) => f.starred),
      };
    case "shared":
      if (!isDemo) return { folders: [], files: [] };
      return {
        folders: folders.filter((f) => f.shared),
        files: files.filter((f) => f.shared),
      };
    case "trash":
      if (!isDemo) return { folders: [], files: [] };
      return { folders: [], files: files.filter((f) => f.trashed) };
  }
}

export function filterBySmartFolder(files: VaultFileItem[], smartFolderId: string | null): VaultFileItem[] {
  if (!smartFolderId) return files;
  const q = smartFolderId.replace("sf-", "");
  return files.filter((f) => {
    const name = f.name.toLowerCase();
    const tags = f.tags ?? [];
    switch (q) {
      case "contracts":
        return name.includes("agreement") || name.includes("contract") || tags.includes("contract");
      case "redlines":
        return name.includes("redline") || tags.includes("redlined");
      case "tax":
        return name.includes("tax") || tags.includes("tax");
      case "press":
        return name.includes("press") || tags.includes("press");
      case "pitch":
        return name.includes("pitch") || name.includes("deck") || tags.includes("pitch");
      default:
        return true;
    }
  });
}

export function enrichDemoFiles(root: VaultFolderNode, sourceId: "eq" | "gdrive" | "dropbox"): void {
  const walk = (node: VaultFolderNode | VaultFileItem, index: { n: number }) => {
    if (isFolder(node)) {
      node.sourceId = sourceId;
      node.modified = node.modified ?? "2 days ago";
      node.children.forEach((c) => walk(c, index));
    } else {
      node.sourceId = sourceId;
      const i = index.n++;
      node.starred = i % 5 === 0;
      node.shared = i % 3 === 0;
      node.trashed = false;
      if (node.name.toLowerCase().includes("contract") || node.name.toLowerCase().includes("agreement")) {
        node.tags = ["contract"];
      } else if (node.name.toLowerCase().includes("tax")) {
        node.tags = ["tax"];
      } else if (node.name.toLowerCase().includes("nda")) {
        node.tags = ["contract", "signature"];
      }
    }
  };
  walk(root, { n: 0 });
}
